/**
 * Math subject constants for Singapore secondary curriculum.
 * A-Math (Additional Mathematics) and E-Math (Elementary Mathematics) apply
 * to G2/G3 students in Sec 3/4. G1, IP, and IB stay single-subject streams
 * without A-Math/E-Math selection.
 */

import type { ExamLevel } from './examLevels.js';
import type { OrgLevel } from './orgLevels.js';

export const MATH_SUBJECTS = Object.freeze(['E-Math', 'A-Math'] as const);

export type MathSubject = (typeof MATH_SUBJECTS)[number];

const APPLICABLE_EXAM_LEVELS: readonly string[] = ['G2', 'G3'];
const APPLICABLE_ORG_LEVELS: readonly string[] = ['secondary_3', 'secondary_4'];

/** Whether math subject selection (A-Math/E-Math) applies for this student's level. */
export function isMathSubjectApplicable(
  examLevel: ExamLevel | string | null | undefined,
  orgLevel: OrgLevel | string | null | undefined
): boolean {
  if (!examLevel || !orgLevel) return false;
  return (
    APPLICABLE_EXAM_LEVELS.includes(examLevel) &&
    APPLICABLE_ORG_LEVELS.includes(orgLevel)
  );
}

/**
 * Untagged is universal: an entity with no `mathSubjects` tag belongs to the
 * common foundation and is visible under either subject focus. The split is
 * real only in Sec 3/4 at G2/G3 (see `isMathSubjectApplicable`), so everything
 * below that line — the whole Sec 1–2 foundation, every G1/IP/IB topic — is
 * correctly untagged and must survive a focused view. A Sec 4 G3 student who
 * focuses on E-Math still needs their Sec 2 algebra remediation.
 *
 * Strict array membership would drop those rows, which is why this predicate
 * exists in one place rather than being respelled per call site. It is the
 * in-memory twin of `buildMathSubjectNodeFilter`
 * (`server/services/mathSubjectNodeFilter.ts`) and of the question-layer
 * `effectiveMathSubjects` clause in `buildQuestionLevelFilter`.
 *
 * No focus requested (`mathSubject` falsy) means no filtering at all.
 */
export function matchesMathSubject(
  tags: readonly string[] | null | undefined,
  mathSubject: MathSubject | string | null | undefined
): boolean {
  if (!mathSubject) return true;
  if (tags === null || tags === undefined || tags.length === 0) return true;
  return tags.includes(mathSubject);
}

/**
 * The paper a student who sits both is served until they choose: E-Math, the
 * one every Sec 3/4 G2/G3 student holds alongside A-Math.
 */
export const DEFAULT_MATH_SUBJECT: MathSubject = 'E-Math';

export interface MathSubjectFocusInput {
  /** The stored choice (`User.focusMathSubject`). Null or absent: never chosen. */
  focusMathSubject?: unknown;
  /** Enrolment truth (`User.mathSubjects`): the papers the student holds. */
  mathSubjects?: readonly string[] | null;
}

export interface ResolvedMathSubjectFocus {
  /**
   * The one paper in force. Null only where there is no paper to serve: the
   * level has no split, or the student holds neither paper (a data fault).
   */
  mathSubject: MathSubject | null;
  /** True when the student chose it; false when the default rule supplied it. */
  explicit: boolean;
}

/**
 * The E-Math / A-Math paper a student is studying, applied by every read of
 * their focus so no surface ever serves both papers interleaved.
 *
 * - No split at the effective level (`isMathSubjectApplicable`): no paper.
 * - A stored choice the student still holds: that paper, explicit.
 * - Otherwise the default: E-Math when they hold it, else the single paper
 *   they hold. A stale choice (a paper they no longer sit, or a value that was
 *   never a paper) falls back the same way, so it never filters out what they
 *   are entitled to.
 *
 * `level` is the effective (orgLevel, examLevel) pair the read is served
 * under, which a level focus may move below the split.
 */
export function resolveMathSubjectFocus(
  student: MathSubjectFocusInput | null | undefined,
  level:
    | {
        orgLevel?: OrgLevel | string | null;
        examLevel?: ExamLevel | string | null;
      }
    | null
    | undefined
): ResolvedMathSubjectFocus {
  if (!isMathSubjectApplicable(level?.examLevel, level?.orgLevel)) {
    return { mathSubject: null, explicit: false };
  }
  const enrolled = Array.isArray(student?.mathSubjects)
    ? student.mathSubjects
    : [];
  const held = MATH_SUBJECTS.filter(subject => enrolled.includes(subject));
  const stored = held.find(subject => subject === student?.focusMathSubject);
  if (stored) return { mathSubject: stored, explicit: true };
  const fallback = held.includes(DEFAULT_MATH_SUBJECT)
    ? DEFAULT_MATH_SUBJECT
    : (held[0] ?? null);
  return { mathSubject: fallback, explicit: false };
}
